import React, { useEffect, useRef } from 'react';
import { CloseOutline, SendOutline } from 'react-ionicons';

import { useChat } from '../../contexts/ChatContext';
import { useConciergeChat } from '../../hooks/useConciergeChat';
import { FILLS, FONTS, GRADIENTS, RADII, STROKES } from '../../styles/tokens';

const ConciergePanel: React.FC = () => {
  const { isChatOpen, closeChat, openChat } = useChat();
  const { messages, input, setInput, isTyping, handleSend, suggestionChips, showSuggestions } =
    useConciergeChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  return (
    <>
      {/* Chat Sidebar */}
      <div
        style={{
          position: 'fixed',
          top: '72px',
          right: 0,
          bottom: 0,
          width: '380px',
          zIndex: 90,
          display: 'flex',
          flexDirection: 'column',
          background: 'rgba(16, 16, 16, 0.94)',
          backdropFilter: 'blur(32px) saturate(1.3)',
          WebkitBackdropFilter: 'blur(32px) saturate(1.3)',
          borderLeft: `1px solid ${STROKES.subtle}`,
          transform: isChatOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Gold top line */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: GRADIENTS.headlineGold,
            opacity: 0.5,
          }}
        />

        {/* Header */}
        <div
          style={{
            padding: '18px 20px',
            borderBottom: `1px solid ${STROKES.subtle}`,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: '34px',
              height: '34px',
              borderRadius: RADII.iconBox,
              background: FILLS.goldAlpha15,
              border: `1px solid ${FILLS.goldAlpha30}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill={FILLS.gold}>
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h4
              style={{
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: FONTS.body,
                color: FILLS.textPrimary,
                margin: 0,
                marginBottom: '1px',
              }}
            >
              AI Concierge
            </h4>
            <p
              style={{
                fontSize: '11px',
                fontFamily: FONTS.body,
                color: FILLS.textMuted,
                margin: 0,
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: FILLS.success,
                  marginRight: '4px',
                }}
              />
              Online — ready to assist
            </p>
          </div>
          <button
            onClick={closeChat}
            aria-label="Close concierge"
            style={{
              width: '28px',
              height: '28px',
              borderRadius: RADII.control,
              border: `1px solid ${STROKES.subtle}`,
              background: 'transparent',
              color: FILLS.textMuted,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = FILLS.goldAlpha30;
              e.currentTarget.style.color = FILLS.gold;
              e.currentTarget.style.background = FILLS.goldAlpha05;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = STROKES.subtle;
              e.currentTarget.style.color = FILLS.textMuted;
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <CloseOutline width="14px" height="14px" color="currentColor" />
          </button>
        </div>

        {/* Messages */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '18px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
          }}
        >
          {messages.map((msg) => (
            <div
              key={msg.id}
              style={{
                alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '88%',
                padding: '11px 16px',
                borderRadius: '14px',
                borderBottomLeftRadius: msg.sender === 'bot' ? '4px' : '14px',
                borderBottomRightRadius: msg.sender === 'user' ? '4px' : '14px',
                fontSize: '13px',
                lineHeight: 1.55,
                fontFamily: FONTS.body,
                background: msg.sender === 'user' ? FILLS.gold : FILLS.chip,
                color: msg.sender === 'user' ? FILLS.page : FILLS.textLight,
                fontWeight: msg.sender === 'user' ? 500 : 400,
                whiteSpace: 'pre-wrap',
              }}
            >
              {msg.text}
            </div>
          ))}

          {isTyping && (
            <div
              style={{
                alignSelf: 'flex-start',
                maxWidth: '88%',
                padding: '11px 16px',
                borderRadius: '14px',
                borderBottomLeftRadius: '4px',
                fontSize: '13px',
                fontFamily: FONTS.body,
                background: FILLS.chip,
                color: FILLS.textMuted,
              }}
            >
              Thinking...
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestion chips */}
        {showSuggestions && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              padding: '0 20px 14px',
              flexShrink: 0,
            }}
          >
            {suggestionChips.map((chip) => (
              <button
                key={chip}
                onClick={() => handleSend(chip)}
                style={{
                  padding: '7px 14px',
                  borderRadius: RADII.pill,
                  border: `1px solid ${STROKES.subtle}`,
                  background: 'transparent',
                  color: FILLS.textMuted,
                  fontFamily: FONTS.body,
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = FILLS.goldAlpha30;
                  e.currentTarget.style.color = FILLS.gold;
                  e.currentTarget.style.background = FILLS.goldAlpha05;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = STROKES.subtle;
                  e.currentTarget.style.color = FILLS.textMuted;
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {chip}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div
          style={{
            padding: '14px 20px',
            borderTop: `1px solid ${STROKES.subtle}`,
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Ask your concierge..."
            style={{
              flex: 1,
              background: FILLS.input,
              border: `1px solid ${STROKES.subtle}`,
              borderRadius: RADII.pill,
              padding: '10px 18px',
              color: FILLS.textPrimary,
              fontFamily: FONTS.body,
              fontSize: '13px',
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = FILLS.goldAlpha30;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = STROKES.subtle;
            }}
          />
          <button
            onClick={() => handleSend()}
            disabled={!input.trim()}
            aria-label="Send message"
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: input.trim() ? FILLS.gold : FILLS.chip,
              border: 'none',
              cursor: input.trim() ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (input.trim()) {
                e.currentTarget.style.background = FILLS.goldWarm;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = input.trim() ? FILLS.gold : FILLS.chip;
            }}
          >
            <SendOutline
              width="16px"
              height="16px"
              color={input.trim() ? FILLS.page : FILLS.textMuted}
            />
          </button>
        </div>
      </div>

      {/* Edge tab — visible when sidebar is closed */}
      <div
        onClick={openChat}
        style={{
          position: 'fixed',
          top: '50%',
          right: 0,
          zIndex: 95,
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '14px 10px 14px 14px',
          background: 'rgba(20, 20, 20, 0.92)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: `1px solid ${FILLS.goldAlpha30}`,
          borderRight: 'none',
          borderRadius: '12px 0 0 12px',
          cursor: 'pointer',
          transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          writingMode: 'vertical-rl',
          color: FILLS.gold,
          fontFamily: FONTS.body,
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          opacity: isChatOpen ? 0 : 1,
          pointerEvents: isChatOpen ? 'none' : 'auto',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.paddingRight = '16px';
          e.currentTarget.style.background = 'rgba(20, 20, 20, 0.97)';
          e.currentTarget.style.borderColor = FILLS.gold;
          e.currentTarget.style.boxShadow = '-8px 0 32px rgba(240, 185, 11, 0.08)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.paddingRight = '10px';
          e.currentTarget.style.background = 'rgba(20, 20, 20, 0.92)';
          e.currentTarget.style.borderColor = FILLS.goldAlpha30;
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill={FILLS.gold}
          style={{ writingMode: 'horizontal-tb' as any }}
        >
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
        <span>Concierge</span>
        <span
          style={{
            display: 'inline-block',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: FILLS.success,
            writingMode: 'horizontal-tb' as any,
            animation: 'dotPulse 2.5s ease-in-out infinite',
          }}
        />
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes dotPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </>
  );
};

export default ConciergePanel;
